// apps/widget/src/screens/intake.screen.tsx
//
// Twenty seconds of questions that feed the contraindication filter and make
// Skin Age mean something.
//
// Every field is skippable. An intake that blocks the scan loses more visitors
// than the accuracy is worth — and an unanswered question becomes a caution at
// booking, not a silent assumption.

import { useState } from 'react';
import { t } from '../lib/i18n';
import { useScan } from '../state';
import { SplitVisual } from '../components/split-visual';

const GOALS = [
  { key: 'wrinkles', label: 'Lines and wrinkles' },
  { key: 'acne', label: 'Breakouts' },
  { key: 'uneven_tone', label: 'Uneven tone' },
  { key: 'laxity', label: 'Firmness' },
  { key: 'periorbital_darkening', label: 'Under-eye area' },
  { key: 'dullness', label: 'Dullness' },
];

/** Matches the API's age gate: the scan is for 16 and over. */
const MIN_AGE = 16;
const MAX_AGE = 90;

/** Whole years on today's date, from a yyyy-mm-dd date of birth. */
export function ageFromDob(dob: string, today = new Date()): number | undefined {
  const [y, m, d] = dob.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age -= 1;
  return age;
}

/** yyyy-mm-dd in the visitor's own calendar — not UTC, which can be a day off. */
const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function IntakeScreen() {
  const { state, dispatch } = useScan();
  // An age or a date of birth — whichever they would rather give.
  const [ageMode, setAgeMode] = useState<'age' | 'dob'>('age');
  // Undefined until the slider is touched: an untouched slider is "skipped",
  // not "35".
  const [age, setAge] = useState<number | undefined>(undefined);
  const [dob, setDob] = useState('');
  const [pregnant, setPregnant] = useState<boolean | undefined>(undefined);
  const [onRetinoids, setOnRetinoids] = useState<boolean | undefined>(undefined);
  const [goal, setGoal] = useState<string | undefined>(undefined);

  const dobAge = dob ? ageFromDob(dob) : undefined;
  const dobValid = dobAge !== undefined && dobAge >= 10 && dobAge <= 120;
  const today = new Date();
  const latest = isoDate(new Date(today.getFullYear() - MIN_AGE, today.getMonth(), today.getDate()));

  const submit = (skip = false): void => {
    const declaredAge = ageMode === 'dob' ? (dobValid ? dobAge : undefined) : age;
    dispatch({
      type: 'intake',
      intake: skip
        ? {}
        : {
            ...(declaredAge !== undefined ? { declaredAge } : {}),
            ...(ageMode === 'dob' && dobValid ? { dob } : {}),
            ...(pregnant !== undefined ? { pregnant } : {}),
            ...(onRetinoids !== undefined ? { onRetinoids } : {}),
            ...(goal ? { goal } : {}),
          },
    });
  };

  return (
    <div className="gs-welcome gs-welcome--form">
      <div className="gs-welcome__copy gs-stack">
      <h1 className="gs-title">{t('intake.title')}</h1>
      <p className="gs-muted">{t('intake.subtitle')}</p>

      <fieldset className="gs-field">
        <legend className="gs-field-label">{t('intake.age')}</legend>

        <div className="gs-segmented" role="radiogroup" aria-label={t('intake.age')}>
          {(['age', 'dob'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={ageMode === mode}
              data-selected={ageMode === mode || undefined}
              onClick={() => setAgeMode(mode)}
            >
              {t(`intake.ageMode.${mode}`)}
            </button>
          ))}
        </div>

        {ageMode === 'age' ? (
          <div className="gs-age">
            <div className="gs-age__value" aria-live="polite">
              {age === undefined ? (
                <span className="gs-age__unset">{t('intake.slideHint')}</span>
              ) : (
                <>
                  <strong>{age >= MAX_AGE ? `${MAX_AGE}+` : age}</strong> {t('intake.years')}
                </>
              )}
            </div>
            <input
              type="range"
              className="gs-range"
              min={MIN_AGE}
              max={MAX_AGE}
              step={1}
              value={age ?? 35}
              data-unset={age === undefined || undefined}
              style={{ '--fill': age === undefined ? '0%' : `${((age - MIN_AGE) / (MAX_AGE - MIN_AGE)) * 100}%` } as React.CSSProperties}
              aria-label={t('intake.age')}
              aria-valuetext={age === undefined ? t('intake.slideHint') : `${age} ${t('intake.years')}`}
              onChange={(e) => setAge(Number(e.target.value))}
              // A tap on the track sets the age even where no "change" fires.
              onPointerDown={(e) => setAge(Number((e.target as HTMLInputElement).value))}
            />
            <div className="gs-age__scale" aria-hidden="true">
              <span>{MIN_AGE}</span>
              <span>{MAX_AGE}+</span>
            </div>
          </div>
        ) : (
          <div className="gs-age">
            <input
              className="gs-input"
              type="date"
              value={dob}
              min="1920-01-01"
              max={latest}
              onChange={(e) => setDob(e.target.value)}
              aria-label={t('intake.dobLabel')}
              aria-invalid={dob !== '' && !dobValid ? true : undefined}
            />
            {dob && (
              <span className={dobValid ? 'gs-age__computed' : 'gs-field-error'} aria-live="polite">
                {dobValid ? t('intake.yearsOld', { age: dobAge! }) : t('intake.dobInvalid')}
              </span>
            )}
          </div>
        )}

        {/* Says why. "Your age" with no reason attached reads as data
            collection; with one, it reads as accuracy. */}
        <span className="gs-field-help">{t('intake.ageHelp')}</span>
      </fieldset>

      <YesNo label={t('intake.pregnant')} value={pregnant} onChange={setPregnant} />
      <YesNo label={t('intake.medications')} value={onRetinoids} onChange={setOnRetinoids} />

      <fieldset className="gs-field">
        <legend className="gs-field-label">{t('intake.goal')}</legend>
        <div className="gs-chips">
          {GOALS.map((option) => (
            <button
              key={option.key}
              type="button"
              className="gs-chip"
              data-selected={goal === option.key || undefined}
              onClick={() => setGoal(goal === option.key ? undefined : option.key)}
            >
              {t(`intake.goals.${option.key}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <button className="gs-btn gs-btn--primary" onClick={() => submit()}>
        {t('intake.continue')}
      </button>

      {/* A real button, not a small link — skipping is a legitimate choice. */}
      <button className="gs-btn gs-btn--ghost" onClick={() => submit(true)}>
        {t('intake.skip')}
      </button>
      </div>

      <SplitVisual config={state.config} />
    </div>
  );
}

function YesNo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
}) {
  return (
    <fieldset className="gs-field">
      <legend className="gs-field-label">{label}</legend>
      <div className="gs-chips">
        <button
          type="button"
          className="gs-chip"
          data-selected={value === true || undefined}
          onClick={() => onChange(value === true ? undefined : true)}
        >
          {t('common.yes')}
        </button>
        <button
          type="button"
          className="gs-chip"
          data-selected={value === false || undefined}
          onClick={() => onChange(value === false ? undefined : false)}
        >
          {t('common.no')}
        </button>
      </div>
    </fieldset>
  );
}
